import { createFileRoute, Link } from '@tanstack/react-router';
import { DocumentUploader } from '../components/DocumentUploader.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { Button } from '../components/ui/button.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.js';
import { useAuth } from '../hooks/useAuth.js';
import { requireRole } from '../lib/auth-guard.js';
import { trpc } from '../lib/trpc.js';

export const Route = createFileRoute('/worker/')({
  component: WorkerDashboard,
  beforeLoad: async ({ context }) => {
    await requireRole(context.queryClient, ['worker']);
  },
});

function WorkerDashboard() {
  const { user, logout } = useAuth();
  const profile = trpc.workers.getMine.useQuery();
  const status = profile.data?.status ?? 'draft';
  const readOnly = status === 'approved' || status === 'blocked' || status === 'pending_review';

  return (
    <div className="min-h-screen p-6 max-w-3xl mx-auto space-y-8">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold">Worker-Bereich</h1>
          <p className="text-[var(--muted-foreground)]">
            {profile.data?.firstName && profile.data?.lastName
              ? `${profile.data.firstName} ${profile.data.lastName}`
              : (user?.name ?? user?.email)}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => logout()}>
          Abmelden
        </Button>
      </header>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle>Mein Profil</CardTitle>
              <CardDescription className="mt-1">
                Status: <StatusBadge status={status} />
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Link to="/worker/profile">
                <Button variant="outline">Profil ansehen</Button>
              </Link>
              {(status === 'draft' || status === 'rejected') && (
                <Link to="/worker/onboarding">
                  <Button>Profil bearbeiten</Button>
                </Link>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {status === 'draft' && (
            <p className="text-sm text-[var(--muted-foreground)]">
              Bitte vervollständige dein Profil. Du kannst die Eingaben jederzeit speichern
              und später fortsetzen.
            </p>
          )}
          {status === 'pending_review' && (
            <p className="text-sm text-[var(--muted-foreground)]">
              Internal Staff prüft dein Profil. Du wirst benachrichtigt, sobald die Prüfung
              abgeschlossen ist.
            </p>
          )}
          {status === 'rejected' && (
            <p className="text-sm text-[oklch(0.55_0.2_25)]">
              Profil abgelehnt. Hinweis: {profile.data?.reviewNotes ?? '—'}. Du kannst die
              Daten anpassen und erneut einreichen.
            </p>
          )}
          {status === 'approved' && (
            <p className="text-sm text-[var(--muted-foreground)]">
              Dein Profil ist freigegeben. Änderungen sind nur noch über Internal Staff
              möglich.
            </p>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Einsätze</CardTitle>
            <CardDescription>Deine Zuweisungen und Einsatzorte</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/worker/assignments">
              <Button variant="outline">Einsätze einsehen</Button>
            </Link>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Abwesenheiten</CardTitle>
            <CardDescription>Urlaub, Krankheit, Sonderurlaub</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/worker/absences">
              <Button variant="outline">Abwesenheiten verwalten</Button>
            </Link>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Stunden</CardTitle>
            <CardDescription>Stundenzettel erfassen</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/worker/timesheets">
              <Button variant="outline">Stunden erfassen</Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {profile.data && <DocumentUploader ownerType="worker" ownerId={profile.data.id} readOnly={readOnly} />}
    </div>
  );
}
